import { useState, useEffect, useCallback, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import { resetSessionExpiredGuard } from "@/lib/server-fn-auth";
import { Sentry } from "@/lib/sentry";
import type { User, Session } from "@supabase/supabase-js";

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<Session | null>;
  signup: (email: string, password: string, fullName: string, extraMeta?: Record<string, unknown>) => Promise<Session | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
        // Sentry user context — id only, no PII.
        if (session?.user) {
          Sentry.setUser({ id: session.user.id });
        } else {
          Sentry.setUser(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      if (session?.user) {
        Sentry.setUser({ id: session.user.id });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    resetSessionExpiredGuard();
    return data.session ?? null;
  }, []);

  const signup = useCallback(async (email: string, password: string, fullName: string, extraMeta?: Record<string, unknown>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName, ...(extraMeta ?? {}) },
        emailRedirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
      },
    });
    if (error) throw error;
    return data.session ?? null;
  }, []);

  const logout = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/reset-password` : undefined,
    });
    if (error) throw error;
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const { error } = await supabase.auth.updateUser({ password });
    if (error) throw error;
  }, []);

  const value: AuthState = {
    isAuthenticated: !!session,
    user,
    session,
    isLoading,
    login,
    signup,
    logout,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const SSR_FALLBACK: AuthState = {
  isAuthenticated: false,
  user: null,
  session: null,
  isLoading: true,
  login: async () => null,
  signup: async () => null,
  logout: async () => {},
  resetPassword: async () => {},
  updatePassword: async () => {},
};

export function useAuth() {
  const context = useContext(AuthContext);
  // During SSR (or if used outside provider), return a safe loading-state default
  // instead of throwing — pages render fine and re-hydrate with real auth on client.
  if (!context) return SSR_FALLBACK;
  return context;
}
